/**
 * What a chip is, as its ROM loader tells it: the chip, its revision, and
 * the details esptool reports. The page and the CLI name a board by these.
 * Reading them from a loader is in `idfweb-chip/loader`.
 */

/** The silicon revision, as ESP-IDF and esptool write it: v3.1 is major 3, minor 1. */
export interface ChipRevision {
    major: number;
    minor: number;
}

export interface ChipIdentity {
    /** The ESP-IDF target name: "esp32c3". */
    target: string;
    /** The chip as esptool-js names it: "ESP32-C3". */
    name: string;
    /** Undefined for a chip esptool-js reads no revision of, such as the ESP8266. */
    revision?: ChipRevision;
    /** The chip's variant and package: "ESP32-D0WD-V3", "ESP32-C3 (QFN32)". */
    description?: string;
    /** "Wi-Fi", "BLE", "Embedded Flash 4MB". */
    features?: string[];
    /** The factory MAC address: "24:0a:c4:12:34:56". */
    mac?: string;
}

/** "v3.1". */
export function revisionLabel(revision: ChipRevision): string {
    return `v${revision.major}.${revision.minor}`;
}

/** How a board is named to a person: "ESP32-C3 v0.4". */
export function chipLabel(chip: ChipIdentity): string {
    return chip.revision === undefined ? chip.name : `${chip.name} ${revisionLabel(chip.revision)}`;
}

/** esptool-js's chip name ("ESP32-C3") as an ESP-IDF target name ("esp32c3"). */
export function targetNameOfChip(chipName: string): string {
    return chipName.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * An ESP-IDF target name ("esp32c3") as the chip name shown in the UI
 * ("ESP32-C3"). The inverse of targetNameOfChip; target names stay internal.
 */
export function chipNameOfTarget(target: string): string {
    return target.toUpperCase().replace(/^(ESP32)(?=.)/, "$1-");
}

/** A 32-bit register of the chip, read through the ROM loader. */
export type ReadRegister = (address: number) => Promise<number>;

/**
 * The revision of the chips esptool-js 0.7.0 reads wrongly: the ESP32 has no
 * major and minor version, and the ESP32-C2 and ESP32-C6 inherit the
 * ESP32-C3's fields. These are the fields esptool's `get_major_chip_version`
 * and `get_minor_chip_version` read.
 */
const REVISIONS: Record<string, (read: ReadRegister) => Promise<ChipRevision>> = {
    esp32: async (read) => {
        const efuse = (word: number) => read(0x3ff5a000 + 4 * word);
        const word3 = await efuse(3);
        const word5 = await efuse(5);
        const apbCtlDate = await read(0x3ff6607c);
        const bits = (((apbCtlDate >>> 31) & 1) << 2) | (((word5 >>> 20) & 1) << 1) | ((word3 >>> 15) & 1);
        const major = ({ 0: 0, 1: 1, 3: 2, 7: 3 } as Record<number, number>)[bits] ?? 0;
        return { major, minor: (word5 >>> 24) & 0x3 };
    },
    esp32c2: async (read) => {
        const word = await read(0x60008840 + 4 * 1);
        return { major: (word >>> 20) & 0x3, minor: (word >>> 16) & 0xf };
    },
    esp32c6: async (read) => {
        const word = await read(0x600b0844 + 4 * 3);
        return { major: (word >>> 22) & 0x3, minor: (word >>> 18) & 0xf };
    },
};

/** The revision of one of those chips, or undefined for another chip. */
export function readRevision(target: string, read: ReadRegister): Promise<ChipRevision> | undefined {
    return REVISIONS[target]?.(read);
}
